import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus, Trash2, Pencil, Upload, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function ContactsDatabase() {
  const [tab, setTab] = useState("people"); // people | organizations
  const [people, setPeople] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const fileRef = useRef(null);

  const reload = async () => {
    const [p, o] = await Promise.all([api.get("/people"), api.get("/organizations")]);
    setPeople(p.data);
    setOrgs(o.data);
  };
  useEffect(() => {
    reload();
  }, []);

  const list = tab === "people" ? people : orgs;
  const sortKey = tab === "people" ? sort.key : sort.key === "name" ? "company_name" : sort.key;

  const filtered = useMemo(() => {
    let l = [...list];
    if (q.trim()) {
      const s = q.toLowerCase();
      l = l.filter((x) => Object.values(x).some((v) => typeof v === "string" && v.toLowerCase().includes(s)));
    }
    l.sort((a, b) => {
      const av = (a[sortKey] || "").toString().toLowerCase();
      const bv = (b[sortKey] || "").toString().toLowerCase();
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return l;
  }, [list, q, sort, sortKey]);

  const toggleSort = (key) => {
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    if (tab === "people") {
      await api.delete(`/people/${id}`);
      setPeople((p) => p.filter((x) => x.id !== id));
    } else {
      await api.delete(`/organizations/${id}`);
      setOrgs((o) => o.filter((x) => x.id !== id));
    }
    toast.success("Deleted");
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/people/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.imported} people`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="px-10 py-9 mr-fade-up">
      <header className="flex items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="font-heading text-3xl text-slate-900 tracking-tight">Contacts Database</h1>
          <p className="text-sm text-slate-500 mt-1 font-body">
            {people.length} people · {orgs.length} organizations
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "people" && (
            <>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onImport} data-testid="csv-import-input" />
              <button
                onClick={() => fileRef.current?.click()}
                className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-heading text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2"
                data-testid="csv-import-button"
              >
                <Upload className="w-4 h-4" />
                Import CSV
              </button>
            </>
          )}
          <button
            onClick={() => {
              setEditing(null);
              setOpenEdit(true);
            }}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-heading font-medium hover:bg-slate-800 inline-flex items-center gap-2"
            data-testid="db-new-button"
          >
            <Plus className="w-4 h-4" />
            New {tab === "people" ? "person" : "organization"}
          </button>
        </div>
      </header>

      <div className="inline-flex bg-white border border-slate-200 rounded-lg p-0.5 mb-5">
        {["people", "organizations"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm rounded-md capitalize font-heading transition-colors ${tab === t ? "bg-slate-900 text-white" : "text-slate-600"}`}
            data-testid={`db-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mr-card p-3 mb-5 flex items-center gap-3">
        <Search className="w-4 h-4 text-slate-400 ml-2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${tab}…`}
          className="flex-1 bg-transparent border-0 outline-none text-sm font-body placeholder:text-slate-400 py-2"
          data-testid="db-search-input"
        />
      </div>

      <div className="mr-card overflow-hidden">
        <table className="w-full text-left">
          <thead className="border-b border-slate-100 bg-slate-50/40">
            {tab === "people" ? (
              <tr>
                <Th onClick={() => toggleSort("name")} active={sort.key === "name"} dir={sort.dir}>Name</Th>
                <Th onClick={() => toggleSort("company")} active={sort.key === "company"} dir={sort.dir}>Company</Th>
                <Th onClick={() => toggleSort("position")} active={sort.key === "position"} dir={sort.dir}>Position</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <th className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 py-3 px-4 w-24"></th>
              </tr>
            ) : (
              <tr>
                <Th onClick={() => toggleSort("company_name")} active={sort.key === "company_name"} dir={sort.dir}>Company</Th>
                <Th onClick={() => toggleSort("industry")} active={sort.key === "industry"} dir={sort.dir}>Industry</Th>
                <Th>Main Contact</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <th className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 py-3 px-4 w-24"></th>
              </tr>
            )}
          </thead>
          <tbody>
            {filtered.map((row) => {
              const isOpen = !!expanded[row.id];
              return (
                <React.Fragment key={row.id}>
                  <tr className="border-b border-slate-50 hover:bg-slate-50/40 transition-colors" data-testid={`db-row-${row.id}`}>
                    {tab === "people" ? (
                      <>
                        <td className="py-3 px-4">
                          <button onClick={() => setExpanded((m) => ({ ...m, [row.id]: !m[row.id] }))} className="inline-flex items-center gap-2 text-slate-900 font-medium hover:text-slate-600">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                            {row.name}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{row.company || "—"}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{row.position || "—"}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{row.email || "—"}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{row.phone || "—"}</td>
                      </>
                    ) : (
                      <>
                        <td className="py-3 px-4">
                          <button onClick={() => setExpanded((m) => ({ ...m, [row.id]: !m[row.id] }))} className="inline-flex items-center gap-2 text-slate-900 font-medium hover:text-slate-600">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                            {row.company_name}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{row.industry || "—"}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{row.main_contact || "—"}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{row.email || "—"}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{row.phone || "—"}</td>
                      </>
                    )}
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setEditing(row);
                            setOpenEdit(true);
                          }}
                          className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                          data-testid={`db-edit-${row.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => remove(row.id)}
                          className="p-1.5 rounded-md text-slate-500 hover:text-rose-500 hover:bg-rose-50"
                          data-testid={`db-delete-${row.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50/40">
                      <td colSpan={6} className="px-6 py-4">
                        <DetailRow row={row} kind={tab} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-16 text-slate-400 font-body text-sm">
                  No matches.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <DbDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        kind={tab}
        item={editing}
        onSaved={() => {
          reload();
          setOpenEdit(false);
        }}
      />
    </div>
  );
}

function Th({ children, onClick, active, dir }) {
  return (
    <th
      onClick={onClick}
      className={`text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 py-3 px-4 ${onClick ? "cursor-pointer select-none" : ""}`}
    >
      <span className={active ? "text-slate-900" : ""}>
        {children} {active ? (dir === "asc" ? "↑" : "↓") : ""}
      </span>
    </th>
  );
}

function DetailRow({ row, kind }) {
  return (
    <div className="grid grid-cols-2 gap-4 text-sm">
      {kind === "people" ? (
        <>
          <Detail label="Tags" value={(row.tags || []).join(", ") || "—"} />
          <Detail label="Notes" value={row.notes || "—"} />
        </>
      ) : (
        <>
          <Detail label="Address" value={row.address || "—"} />
          <Detail label="Notes" value={row.notes || "—"} />
        </>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 mb-1 font-heading">{label}</div>
      <div className="text-slate-700 whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function DbDialog({ open, onClose, kind, item, onSaved }) {
  const isEdit = !!item?.id;
  const [form, setForm] = useState({});
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (kind === "people") {
      setForm(
        item || { name: "", phone: "", email: "", company: "", position: "", tags: [], notes: "" },
      );
    } else {
      setForm(
        item || { company_name: "", industry: "", address: "", main_contact: "", phone: "", email: "", notes: "" },
      );
    }
    setTagInput("");
  }, [open, item, kind]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (kind === "people" && !(form.name || "").trim()) {
      toast.error("Name is required");
      return;
    }
    if (kind === "organizations" && !(form.company_name || "").trim()) {
      toast.error("Company name is required");
      return;
    }
    setBusy(true);
    try {
      const endpoint = kind === "people" ? "/people" : "/organizations";
      const payload = { ...form };
      delete payload.id;
      delete payload.created_at;
      if (isEdit) await api.put(`${endpoint}/${item.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(isEdit ? "Updated" : "Created");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="!max-w-lg bg-white rounded-2xl border-slate-100 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="font-heading text-xl text-slate-900">
            {isEdit ? "Edit" : "New"} {kind === "people" ? "person" : "organization"}
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {kind === "people" ? (
            <>
              <Input label="Name" required value={form.name} onChange={(v) => set("name", v)} testid="db-name-input" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
                <Input label="Email" value={form.email} onChange={(v) => set("email", v)} />
                <Input label="Company" value={form.company} onChange={(v) => set("company", v)} />
                <Input label="Position" value={form.position} onChange={(v) => set("position", v)} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">Tags</label>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {(form.tags || []).map((t) => (
                    <span key={t} className="mr-pill bg-slate-50 border-slate-200 text-slate-700">
                      {t}
                      <button onClick={() => set("tags", form.tags.filter((x) => x !== t))} className="text-slate-400">×</button>
                    </span>
                  ))}
                  <input
                    className="mr-input flex-1 min-w-[140px]"
                    placeholder="Type and press Enter"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const t = tagInput.trim();
                        if (t && !(form.tags || []).includes(t)) set("tags", [...(form.tags || []), t]);
                        setTagInput("");
                      }
                    }}
                  />
                </div>
              </div>
              <Input label="Notes" multiline value={form.notes} onChange={(v) => set("notes", v)} />
            </>
          ) : (
            <>
              <Input label="Company name" required value={form.company_name} onChange={(v) => set("company_name", v)} testid="db-company-input" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Industry" value={form.industry} onChange={(v) => set("industry", v)} />
                <Input label="Main contact" value={form.main_contact} onChange={(v) => set("main_contact", v)} />
                <Input label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
                <Input label="Email" value={form.email} onChange={(v) => set("email", v)} />
              </div>
              <Input label="Address" value={form.address} onChange={(v) => set("address", v)} />
              <Input label="Notes" multiline value={form.notes} onChange={(v) => set("notes", v)} />
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 font-heading">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium font-heading hover:bg-slate-800 disabled:opacity-60" data-testid="db-save-button">
            {busy ? "Saving…" : isEdit ? "Save" : "Create"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Input({ label, required, value, onChange, multiline, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {multiline ? (
        <textarea data-testid={testid} className="mr-input mt-1.5 min-h-[80px]" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input data-testid={testid} className="mr-input mt-1.5" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
